import { create as bufCreate } from '@bufbuild/protobuf';
import { v4 as uuid4 } from 'uuid';

import { HandoffSchema } from '@dbt-labs/proto/public/events/metadata_telemetry_pb';

import { getVortexClient, VortexEvent } from '@/analytics/vortex';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';

export type HandoffAssetDbtType =
  | 'dbt_model'
  | 'dbt_source'
  | 'dbt_snapshot'
  | 'dbt_seed'
  | 'dbt_metric'
  | 'dbt_saved_query'
  | 'unknown';

export type HandoffAssetDataWarehouseType =
  | 'data_warehouse_table'
  | 'data_warehouse_view'
  | 'unknown';

export type HandoffAssetType = HandoffAssetDbtType | HandoffAssetDataWarehouseType;

interface HandoffEventParams {
  handoff_from: 'catalog';
  handoff_to: 'insights' | 'canvas' | 'ide';
  is_final_handoff_action: boolean;
  handoff_action?: string | undefined;
  asset_type: HandoffAssetType;
}
interface HandoffEvent extends VortexEvent, HandoffEventParams {}

async function sendFullHandoffEvent(event: HandoffEvent) {
  const msg = bufCreate(HandoffSchema, {
    context: {
      eventId: uuid4(),
      sessionId: event.session_id,
      dbtCloudAccountId: event.dbt_cloud_account_id.toString(),
      dbtCloudAccountIdentifier: event.dbt_cloud_account_identifier,
      dbtCloudProjectId: event.dbt_cloud_project_id?.toString(),
      dbtCloudEnvironmentId: event.dbt_cloud_environment_id?.toString(),
      dbtCloudUserId: event.dbt_cloud_user_id.toString(),
      referrerUrl: event.referrer_url,
    },
    handoffFrom: event.handoff_from,
    handoffTo: event.handoff_to,
    isFinalHandoffAction: event.is_final_handoff_action,
    handoffAction: event.handoff_action,
    assetType: event.asset_type,
  });

  const vortexClient = getVortexClient();
  await vortexClient.logProto(HandoffSchema, msg);
  await vortexClient.flush();
}

export const useHandoffEventAccount = () => {
  const { accountId, account, userId } = useDbtCloudAccount();

  /**
   * Function to send a handoff event to Vortex given just the handoff_to and is_final_handoff_action params.
   */
  const sendHandoffEventAccount = (
    params: Omit<HandoffEventParams, 'handoff_from'>,
  ) => {
    const { handoff_to, is_final_handoff_action, handoff_action, asset_type } = params;
    sendFullHandoffEvent({
      dbt_cloud_account_id: accountId || 0,
      dbt_cloud_account_identifier: account?.identifier || '',
      dbt_cloud_user_id: userId === 'loading' ? 0 : userId,
      referrer_url: window.location.href,
      handoff_from: 'catalog',
      handoff_to,
      is_final_handoff_action,
      handoff_action,
      asset_type,
      // TODO: populate below
      session_id: undefined,
      snowplow_domain_session_id: undefined,
      snowplow_domain_user_id: undefined,
    });
  };

  return { sendHandoffEventAccount };
};

export const useHandoffEventProject = ({
  projectId,
  environmentId,
}: {
  projectId?: number;
  environmentId?: number;
} = {}) => {
  const { accountId = 0, account } = useDbtCloudAccount();
  const {
    projectId: inheritedProjectId,
    environmentId: inheritedEnvironmentId,
    userId,
  } = useDbtCloudProject();
  const validProjectId = projectId ?? inheritedProjectId ?? 0;
  const validEnvironmentId = environmentId ?? inheritedEnvironmentId ?? 0;

  /**
   * Function to send a handoff event to Vortex given just the handoff_to and is_final_handoff_action params.
   */
  const sendHandoffEventProject = (
    params: Omit<HandoffEventParams, 'handoff_from'>,
  ) => {
    const { handoff_to, is_final_handoff_action, handoff_action, asset_type } = params;
    sendFullHandoffEvent({
      dbt_cloud_account_id: accountId,
      dbt_cloud_account_identifier: account?.identifier || '',
      dbt_cloud_user_id: userId === 'loading' ? 0 : userId,
      dbt_cloud_project_id: validProjectId,
      dbt_cloud_environment_id: validEnvironmentId,
      referrer_url: window.location.href,
      handoff_from: 'catalog',
      handoff_to,
      is_final_handoff_action,
      handoff_action,
      asset_type,
      // TODO: populate below
      session_id: undefined,
      snowplow_domain_session_id: undefined,
      snowplow_domain_user_id: undefined,
    });
  };

  return { sendHandoffEventProject };
};
